import { getContactUsFormTemplate } from "../../shortcodes/templates";
import { getShortcodeWrapper, addShortcodeToEditor } from "../../shortodeAddition";
import { parseToHtml } from "@/utility/parseToHtml";

const DEFAULT_CONFIG = {
  sendEmailToGroove: false,
  buttonText: "Send Message",
  buttonBgColor: "#1B1B1B",
  buttonTextColor: "#FFFFFF",
  successMessage: "Your message sent!",
  errorMessage: "Your message cannot be sent.",
  showSideBlock: true,
  sideBlockBgColor: "#1B1B1B",
  sideBlockTextColor: "#FFFFFF",
  sidebarTitle: "Contact Information",
  sidebarSubtitle: "Say something to start a live chat!",
  phone: "+1012 3456 789",
  email: "demo@gmail.com",
  address: "132 Dartmouth Street Boston, Massachusetts 02156 United States",
  company: "Improve Insurance Company",
  nameLabel: "Full Name",
  emailLabel: "Email Address",
  messageLabel: "Message",
  namePlaceholder: "",
  emailPlaceholder: "",
  messagePlaceholder: "Type your message here...",
};

const decodeFromContactUsFormData = element => {
  if (!element) return {};

  try {
    return JSON.parse(decodeURIComponent(unescape(window.atob(element))));
  } catch (e) {
    return {};
  }
};

const extractDataFromElement = element => {
  const data = { ...DEFAULT_CONFIG };

  // Extract text content from data-tiny-mce-component-data elements
  const sidebarTitle = element.querySelector('[data-tiny-mce-component-data="sidebar-title"]');
  if (sidebarTitle) data.sidebarTitle = sidebarTitle.textContent.trim();

  const sidebarSubtitle = element.querySelector('[data-tiny-mce-component-data="sidebar-subtitle"]');
  if (sidebarSubtitle) data.sidebarSubtitle = sidebarSubtitle.textContent.trim();

  const phone = element.querySelector('[data-tiny-mce-component-data="phone"]');
  if (phone) data.phone = phone.textContent.trim();

  const email = element.querySelector('[data-tiny-mce-component-data="email"]');
  if (email) data.email = email.textContent.trim();

  const address = element.querySelector('[data-tiny-mce-component-data="address"]');
  if (address) data.address = address.textContent.trim();

  const company = element.querySelector('[data-tiny-mce-component-data="company"]');
  if (company) data.company = company.textContent.trim();

  const buttonText = element.querySelector('[data-tiny-mce-component-data="button-text"]');
  if (buttonText) data.buttonText = buttonText.textContent.trim();

  // Extract colors from style attributes
  const sidebar = element.querySelector(".contact-us-form__sidebar");
  if (sidebar) {
    data.showSideBlock = true;
    const bgMatch = sidebar.style.backgroundColor;
    const colorMatch = sidebar.style.color;
    if (bgMatch) data.sideBlockBgColor = rgbToHex(bgMatch) || bgMatch;
    if (colorMatch) data.sideBlockTextColor = rgbToHex(colorMatch) || colorMatch;
  } else {
    data.showSideBlock = false;
  }

  const button = element.querySelector(".contact-us-form__button");
  if (button) {
    const bgMatch = button.style.backgroundColor;
    const colorMatch = button.style.color;
    if (bgMatch) data.buttonBgColor = rgbToHex(bgMatch) || bgMatch;
    if (colorMatch) data.buttonTextColor = rgbToHex(colorMatch) || colorMatch;
  }

  // Extract data attributes for form settings
  const storedData = element.closest(".shortcode")?.getAttribute("data-contact-us-form-data");
  if (storedData) {
    const decoded = decodeFromContactUsFormData(storedData);
    if (decoded.sendEmailToGroove !== undefined) data.sendEmailToGroove = decoded.sendEmailToGroove;
    if (decoded.successMessage) data.successMessage = decoded.successMessage;
    if (decoded.errorMessage) data.errorMessage = decoded.errorMessage;
    if (decoded.nameLabel) data.nameLabel = decoded.nameLabel;
    if (decoded.emailLabel) data.emailLabel = decoded.emailLabel;
    if (decoded.messageLabel) data.messageLabel = decoded.messageLabel;
    if (decoded.namePlaceholder !== undefined) data.namePlaceholder = decoded.namePlaceholder;
    if (decoded.emailPlaceholder !== undefined) data.emailPlaceholder = decoded.emailPlaceholder;
    if (decoded.messagePlaceholder !== undefined) data.messagePlaceholder = decoded.messagePlaceholder;
  }

  return data;
};

// Helper function to convert rgb to hex
const rgbToHex = rgb => {
  if (!rgb) return null;
  if (rgb.startsWith("#")) return rgb;

  const result = rgb.match(/\d+/g);
  if (!result || result.length < 3) return null;

  const toHex = n => {
    const hex = parseInt(n).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return "#" + toHex(result[0]) + toHex(result[1]) + toHex(result[2]);
};

const getDialogConfig = (initialData = {}) => {
  const data = { ...DEFAULT_CONFIG, ...initialData };

  return {
    title: "Contact Us Form",
    body: {
      type: "panel",
      items: [
        {
          type: "checkbox",
          name: "sendEmailToGroove",
          label: "Send email to Groove",
        },
        {
          type: "input",
          name: "buttonText",
          label: "Button text",
        },
        {
          type: "colorinput",
          name: "buttonBgColor",
          label: "Button background color",
        },
        {
          type: "colorinput",
          name: "buttonTextColor",
          label: "Button text color",
        },
        {
          type: "input",
          name: "successMessage",
          label: "Success message",
        },
        {
          type: "input",
          name: "errorMessage",
          label: "Error message",
        },
        {
          type: "checkbox",
          name: "showSideBlock",
          label: "Show contact info sidebar",
        },
        {
          type: "colorinput",
          name: "sideBlockBgColor",
          label: "Sidebar background color",
        },
        {
          type: "colorinput",
          name: "sideBlockTextColor",
          label: "Sidebar text color",
        },
        {
          type: "input",
          name: "nameLabel",
          label: "Name field label",
        },
        {
          type: "input",
          name: "emailLabel",
          label: "Email field label",
        },
        {
          type: "input",
          name: "messageLabel",
          label: "Message field label",
        },
        {
          type: "input",
          name: "namePlaceholder",
          label: "Name field placeholder",
        },
        {
          type: "input",
          name: "emailPlaceholder",
          label: "Email field placeholder",
        },
        {
          type: "input",
          name: "messagePlaceholder",
          label: "Message field placeholder",
        },
      ],
    },
    initialData: {
      sendEmailToGroove: data.sendEmailToGroove,
      buttonText: data.buttonText,
      buttonBgColor: data.buttonBgColor,
      buttonTextColor: data.buttonTextColor,
      successMessage: data.successMessage,
      errorMessage: data.errorMessage,
      showSideBlock: data.showSideBlock,
      sideBlockBgColor: data.sideBlockBgColor,
      sideBlockTextColor: data.sideBlockTextColor,
      nameLabel: data.nameLabel,
      emailLabel: data.emailLabel,
      messageLabel: data.messageLabel,
      namePlaceholder: data.namePlaceholder,
      emailPlaceholder: data.emailPlaceholder,
      messagePlaceholder: data.messagePlaceholder,
    },
  };
};

const editContactUsFormConfig = (editor, shortcodeWrapper) => {
  const contactUsFormElement = shortcodeWrapper.querySelector('[data-tiny-mce-component="contact-us-form"]');
  const currentData = contactUsFormElement ? extractDataFromElement(contactUsFormElement) : { ...DEFAULT_CONFIG };

  const dialogConfig = getDialogConfig(currentData);

  editor.windowManager.open({
    ...dialogConfig,
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "submit",
        name: "submit",
        text: "Save",
        primary: true,
      },
    ],
    onSubmit: api => {
      const formData = api.getData();

      // Merge with existing content data (preserve inline-edited text)
      const mergedData = {
        ...currentData,
        ...formData,
      };

      // Generate new template with updated options
      const newTemplate = getContactUsFormTemplate(mergedData);

      // Preserve the remove button
      const removeButton = shortcodeWrapper.querySelector('[data-shortcode-remove-onclick]');
      const removeButtonHtml = removeButton ? removeButton.outerHTML : '<div data-shortcode-remove-onclick="true">x</div>';

      shortcodeWrapper.innerHTML = removeButtonHtml + newTemplate;

      // Store the form settings in data attribute
      const formSettings = {
        sendEmailToGroove: formData.sendEmailToGroove,
        successMessage: formData.successMessage,
        errorMessage: formData.errorMessage,
        buttonBgColor: formData.buttonBgColor,
        buttonTextColor: formData.buttonTextColor,
        showSideBlock: formData.showSideBlock,
        sideBlockBgColor: formData.sideBlockBgColor,
        sideBlockTextColor: formData.sideBlockTextColor,
        nameLabel: formData.nameLabel,
        emailLabel: formData.emailLabel,
        messageLabel: formData.messageLabel,
        namePlaceholder: formData.namePlaceholder,
        emailPlaceholder: formData.emailPlaceholder,
        messagePlaceholder: formData.messagePlaceholder,
      };

      shortcodeWrapper.setAttribute(
        "data-contact-us-form-data",
        window.btoa(escape(encodeURIComponent(JSON.stringify(formSettings)))),
      );

      editor.fire("change");
      api.close();
    },
  });
};

const contactUsFormConfig = (tinymce, uuid, shortcodeOnClick) => {
  const dialogConfig = getDialogConfig();

  tinymce.activeEditor.windowManager.open({
    ...dialogConfig,
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "submit",
        name: "submit",
        text: "Add",
        primary: true,
      },
    ],
    onSubmit: api => {
      const formData = api.getData();

      const shortcodeWrapper = getShortcodeWrapper(
        uuid,
        shortcodeOnClick,
        getContactUsFormTemplate(formData),
        "contact-us-form",
      );

      const shortcodeWrapperEl = parseToHtml(shortcodeWrapper).querySelector(".shortcode");

      // Store the form settings in data attribute
      const formSettings = {
        sendEmailToGroove: formData.sendEmailToGroove,
        successMessage: formData.successMessage,
        errorMessage: formData.errorMessage,
        buttonBgColor: formData.buttonBgColor,
        buttonTextColor: formData.buttonTextColor,
        showSideBlock: formData.showSideBlock,
        sideBlockBgColor: formData.sideBlockBgColor,
        sideBlockTextColor: formData.sideBlockTextColor,
        nameLabel: formData.nameLabel,
        emailLabel: formData.emailLabel,
        messageLabel: formData.messageLabel,
        namePlaceholder: formData.namePlaceholder,
        emailPlaceholder: formData.emailPlaceholder,
        messagePlaceholder: formData.messagePlaceholder,
      };

      shortcodeWrapperEl.setAttribute(
        "data-contact-us-form-data",
        window.btoa(escape(encodeURIComponent(JSON.stringify(formSettings)))),
      );

      addShortcodeToEditor(shortcodeWrapperEl.outerHTML, uuid);

      api.close();
    },
  });
};

export { contactUsFormConfig, editContactUsFormConfig, DEFAULT_CONFIG, extractDataFromElement };
